import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Building2, ChevronRight, Clock, FileText, Shield, CheckCircle, ArrowLeft, Info, Save, Play, AlertTriangle, AlertCircle, Check, Download, Share2, Briefcase, Users, Home, FileCheck, Globe, Sparkles, Plus, Loader2, Zap, Timer, Heart, Scale, Plane, FileWarning, HelpCircle, Copy, Archive, RefreshCw } from 'lucide-react';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DownloadReportModal } from '../components/modals/DownloadReportModal';
import { ShareReportModal } from '../components/modals/ShareReportModal';
interface DashboardContext {
  toggleMobileSidebar: () => void;
}
type ViewState = 'landing' | 'setup' | 'wizard' | 'results' | 'action-plan' | 'history';
type AssessmentType = 'personal' | 'business';
// Helper icon component
function Scroll(props: any) {
  return <FileText {...props} data-id="element-2294" />;
}
const PERSONAL_SECTIONS = [{
  id: 'family',
  title: 'Family & Relationship Changes',
  icon: Heart,
  whyItMatters: 'Family law varies by province. Formal agreements like Cohabitation or Separation Agreements provide certainty and protect your assets, while documented guardianship wishes ensure your children are cared for by people you choose.',
  commonlyOverlooked: 'Common-law partners do not automatically have the same property rights as married spouses in many provinces.',
  whenToReview: 'After marriage, separation, birth of a child, or when a dependant requires special care.'
}, {
  id: 'estate',
  title: 'Wills, POA & Estate Planning',
  icon: Scroll,
  whyItMatters: 'In Canada, dying without a Will (intestate) means provincial laws dictate how your assets are divided, which may not align with your wishes. A Power of Attorney ensures someone you trust can manage your finances and healthcare if you become incapacitated.',
  commonlyOverlooked: 'Digital assets (crypto, social media, online accounts) are often forgotten in estate planning. Beneficiary designations on RRSPs/TFSAs override your Will.',
  whenToReview: 'Every 3-5 years, or after a major life event like marriage, divorce, or the death of an executor.'
}, {
  id: 'property',
  title: 'Property & Housing',
  icon: Home,
  whyItMatters: 'How you structure property ownership (e.g., Joint Tenancy vs. Tenants in Common) drastically affects what happens to the property when an owner passes away. For renters, a written lease protects your rights under provincial tenancy acts.',
  commonlyOverlooked: 'Title fraud is a growing risk; title insurance can protect you even years after purchasing a home.',
  whenToReview: 'When buying/selling, renewing a mortgage, or changing who lives in the property.'
}, {
  id: 'work_money',
  title: 'Work, Money & Disputes',
  icon: Briefcase,
  whyItMatters: 'A clear employment contract protects your rights regarding severance and termination. Personal liability insurance acts as a crucial safety net against unforeseen lawsuits or claims that could jeopardize your personal assets.',
  commonlyOverlooked: 'Independent contractors may actually be employees under the law, entitling them to severance and benefits.',
  whenToReview: 'Starting a new job, facing workplace conflict, or dealing with significant debt.'
}, {
  id: 'special',
  title: 'International & Special Situations',
  icon: Plane,
  whyItMatters: 'Cross-border assets, immigration status, and international travel with children require specific legal documentation to avoid border delays, tax complications, or status issues.',
  commonlyOverlooked: 'Traveling internationally with a child without the other parent requires a formal consent letter.',
  whenToReview: 'Planning extended travel, sponsoring family, or acquiring assets outside Canada.'
}, {
  id: 'readiness',
  title: 'Personal Legal Readiness',
  icon: Shield,
  whyItMatters: 'Having legal documents is only half the battle; they must be accessible when needed. Storing them securely and ensuring your executor or attorney knows where they are prevents delays and added stress during emergencies.',
  commonlyOverlooked: 'Keeping the only original copy of a Will in a bank safety deposit box can cause issues if the executor cannot access the box after death.',
  whenToReview: 'Annually, to ensure documents are organized and contacts are up to date.'
}];
const BUSINESS_SECTIONS = [{
  id: 'setup',
  title: 'Business Setup & Ownership',
  icon: Building2,
  whyItMatters: 'Proper incorporation and a Shareholders Agreement protect your personal assets from business liabilities. Up-to-date minute books are legally required in Canada and essential for securing financing or selling your business.',
  commonlyOverlooked: 'Failing to file annual returns can lead to administrative dissolution of your corporation.',
  whenToReview: 'Starting up, bringing on new partners, or seeking investment.'
}, {
  id: 'finance',
  title: 'Financial Commitments & Liability',
  icon: Scale,
  whyItMatters: 'Personal guarantees and indemnities can pierce the corporate veil, putting your personal home and savings at risk for business debts.',
  commonlyOverlooked: 'Signing a commercial lease often requires a personal guarantee that outlasts your involvement in the business.',
  whenToReview: 'Taking on debt, signing commercial leases, or entering major vendor agreements.'
}, {
  id: 'contracts',
  title: 'Contracts, Commercial Terms & IP',
  icon: FileCheck,
  whyItMatters: 'Relying on informal or verbal agreements leaves your business vulnerable to disputes and non-payment. Standardized, legally reviewed contracts set clear expectations and protect your commercial interests.',
  commonlyOverlooked: 'Using templates from the US or other jurisdictions may include clauses that are unenforceable in Canada.',
  whenToReview: 'Launching new services, entering new markets, or creating original IP.'
}, {
  id: 'hr',
  title: 'People & Workplace Risk',
  icon: Users,
  whyItMatters: 'Misclassifying employees as independent contractors can lead to severe CRA penalties. Clear employment contracts and up-to-date policies ensure compliance with provincial Employment Standards.',
  commonlyOverlooked: 'Without a written termination clause, employees are entitled to common law reasonable notice, which can be up to 24 months of pay.',
  whenToReview: 'Hiring first employees, terminating staff, or updating compensation.'
}, {
  id: 'privacy',
  title: 'Privacy, Operations & Compliance',
  icon: Globe,
  whyItMatters: 'If you collect customer data, a PIPEDA-compliant Privacy Policy is mandatory. Adequate commercial general liability (CGL) and professional liability (E&O) insurance are critical to shield your business from lawsuits.',
  commonlyOverlooked: "Canada's Anti-Spam Legislation (CASL) requires express or implied consent before sending commercial emails.",
  whenToReview: 'Launching a website, collecting new data types, or expanding operations.'
}, {
  id: 'readiness',
  title: 'Business Legal Readiness',
  icon: Activity,
  whyItMatters: 'Organized legal records and proactive compliance tracking prevent minor administrative issues from becoming major legal hurdles during audits or due diligence.',
  commonlyOverlooked: 'Missing renewal deadlines for licenses or insurance can halt operations immediately.',
  whenToReview: 'Annually, or before a major transaction like a sale or audit.'
}];
const PERSONAL_QUESTIONS = [[{
  id: 'p_fam_1',
  text: 'Has your family situation changed recently (e.g., marriage, common-law, separation, new child)?',
  context: 'Major life events often invalidate existing legal planning or create new legal obligations.',
  options: ['Yes, recently changed', 'Anticipating a change soon', 'No changes'],
  scores: [40, 60, 100]
}, {
  id: 'p_fam_2',
  text: 'If living with a partner, do you have a formal Cohabitation Agreement or Prenuptial Agreement?',
  context: 'Common-law relationships have different property rights than marriages in most provinces. An agreement clarifies asset division.',
  options: ['Yes', 'No', 'Not applicable'],
  scores: [100, 0, 100]
}, {
  id: 'p_fam_3',
  text: 'If you are separated or divorced, do you have a finalized, legally binding Separation Agreement?',
  context: 'A Separation Agreement provides certainty on property division, support, and custody. Without one, disputes can drag on.',
  options: ['Yes', 'No', 'In progress', 'Not applicable'],
  scores: [100, 0, 50, 100]
}, {
  id: 'p_fam_4',
  text: 'Have you legally documented guardianship wishes for your minor children?',
  context: 'If both parents die without naming a guardian, the court decides who raises your children.',
  options: ['Yes', 'No', 'Not applicable'],
  scores: [100, 0, 100]
}], [{
  id: 'p_est_1',
  text: 'Do you have a signed Last Will and Testament that reflects your current wishes?',
  context: "Without a valid Will, your province's intestacy laws determine how your assets are distributed.",
  options: ['Yes, fully updated', 'Yes, but it needs updating', 'No, not yet'],
  scores: [100, 50, 0]
}, {
  id: 'p_est_2',
  text: 'Have you appointed a Power of Attorney for Property to manage your finances if you become incapacitated?',
  context: 'If you become incapacitated without a POA, someone must apply to the court to manage your finances.',
  options: ['Yes, legally documented', 'No', 'I am not sure'],
  scores: [100, 0, 0]
}, {
  id: 'p_est_3',
  text: 'Have you appointed a Power of Attorney for Personal Care (or Personal Directive) for healthcare decisions?',
  context: 'Without this, your family may face difficult decisions about your medical treatment without clear guidance.',
  options: ['Yes, legally documented', 'No', 'I am not sure'],
  scores: [100, 0, 0]
}, {
  id: 'p_est_4',
  text: 'Have you considered how your digital assets (online accounts, crypto, digital files) should be handled?',
  context: 'Digital assets are often lost or inaccessible to executors without specific instructions.',
  options: ['Yes, instructions are documented', 'No', 'Not applicable'],
  scores: [100, 0, 100]
}], [{
  id: 'p_prop_1',
  text: 'Is your home ownership structured jointly (Joint Tenancy) or individually (Tenants in Common)?',
  context: 'Joint Tenancy means the property automatically passes to the surviving owner, bypassing your Will. Tenants in Common allows you to leave your share to anyone.',
  options: ['Joint Tenancy', 'Tenants in Common', 'Sole Owner', 'I rent / Not applicable'],
  scores: [100, 100, 100, 100]
}, {
  id: 'p_prop_2',
  text: 'Are you planning to buy, sell, or refinance real estate in the next 12 months?',
  context: 'Real estate transactions require legal review of purchase agreements, title searches, and mortgage documents.',
  options: ['Yes', 'No'],
  scores: [50, 100]
}, {
  id: 'p_prop_3',
  text: 'If you rent, do you have a written lease agreement that complies with your provincial Residential Tenancies Act?',
  context: 'A written lease protects your rights as a tenant, including rules around rent increases and eviction procedures.',
  options: ['Yes', 'No', 'Not applicable'],
  scores: [100, 0, 100]
}, {
  id: 'p_prop_4',
  text: 'If you are a landlord, do you use standardized, legally compliant lease agreements?',
  context: 'Using outdated or non-compliant leases can make it difficult to enforce rules or evict problem tenants.',
  options: ['Yes', 'No', 'Not applicable'],
  scores: [100, 0, 100]
}], [{
  id: 'p_work_1',
  text: 'Do you have a written employment contract outlining severance and termination terms?',
  context: 'Without a written contract, you may be entitled to common law reasonable notice, which is often more generous than statutory minimums.',
  options: ['Yes', 'No', 'I am self-employed/retired'],
  scores: [100, 0, 100]
}, {
  id: 'p_work_2',
  text: 'Are you currently facing major debt, collection pressure, or considering bankruptcy?',
  context: 'Unresolved debt can lead to wage garnishment or asset seizure. Legal options exist to manage or restructure debt.',
  options: ['Yes', 'No'],
  scores: [0, 100]
}, {
  id: 'p_work_3',
  text: 'Are you involved in, or anticipating, a personal dispute or lawsuit (e.g., personal injury, contract dispute)?',
  context: 'Early legal intervention in disputes can often prevent costly litigation and secure better settlements.',
  options: ['Yes, currently involved', 'Anticipating one', 'No'],
  scores: [0, 40, 100]
}], [{
  id: 'p_spec_1',
  text: 'Are you planning to sponsor family members or foreign nationals for Canadian immigration?',
  context: 'Sponsorship involves strict legal and financial commitments that last for years, even if relationships change.',
  options: ['Yes', 'No'],
  scores: [50, 100]
}, {
  id: 'p_spec_2',
  text: 'Do you own property or significant assets outside of Canada?',
  context: 'Cross-border assets may require a separate Will in that jurisdiction and have complex tax implications.',
  options: ['Yes', 'No'],
  scores: [40, 100]
}, {
  id: 'p_spec_3',
  text: 'Are you a primary caregiver for a vulnerable dependant or adult with special needs?',
  context: 'Special needs trusts (Henson Trusts) and specific guardianship planning are required to protect their government benefits.',
  options: ['Yes', 'No'],
  scores: [50, 100]
}], [{
  id: 'p_read_1',
  text: 'Are your important legal documents stored securely and accessible to your executor or designated attorney?',
  context: 'If your executor cannot find your Will or POA documents when needed, it causes delays, added costs, and stress.',
  options: ['Yes', 'No', 'Some of them'],
  scores: [100, 0, 50]
}, {
  id: 'p_read_2',
  text: 'Would your family or emergency contacts know where to find your key legal documents if needed?',
  context: 'Communication is key. A perfectly drafted Will is useless if no one knows it exists or where to find it.',
  options: ['Yes, they know exactly where', 'They have a general idea', 'No'],
  scores: [100, 50, 0]
}, {
  id: 'p_read_3',
  text: 'How confident are you in your overall legal readiness if something unexpected happens tomorrow?',
  context: 'Confidence usually correlates with having organized, up-to-date legal protections in place.',
  options: ['Very confident', 'Somewhat confident', 'Not confident'],
  scores: [100, 50, 0]
}]];
const BUSINESS_QUESTIONS = [[{
  id: 'b_set_1',
  text: 'Is your business formally incorporated federally or provincially in Canada?',
  context: 'Incorporation creates a separate legal entity, protecting your personal assets from business liabilities.',
  options: ['Yes, incorporated', 'No, sole proprietorship', 'No, general partnership'],
  scores: [100, 40, 30]
}, {
  id: 'b_set_2',
  text: 'If you have co-founders or partners, do you have a signed Shareholders or Partnership Agreement?',
  context: 'Without an agreement, provincial default rules apply, which can lead to deadlock or costly disputes when partners disagree.',
  options: ['Yes, drafted by a lawyer', 'Yes, but it is informal', 'No', 'Not applicable (sole owner)'],
  scores: [100, 40, 0, 100]
}, {
  id: 'b_set_3',
  text: 'Are your corporate minute books and annual returns up to date with the government registry?',
  context: 'Falling behind can result in administrative dissolution, loss of limited liability protection, and difficulty securing financing.',
  options: ['Yes, fully up to date', 'No, we are behind', 'I do not know what that is', 'Not applicable'],
  scores: [100, 30, 0, 100]
}], [{
  id: 'b_fin_1',
  text: "Have you been asked to personally guarantee someone else's business debt or a commercial lease?",
  context: 'Personal guarantees bypass corporate limited liability, putting your personal assets (like your home) at risk if the business defaults.',
  options: ['Yes, and I signed it', 'Yes, but I am reviewing it', 'No'],
  scores: [30, 60, 100]
}, {
  id: 'b_fin_2',
  text: 'Have you signed agreements requiring you to indemnify another party?',
  context: "Indemnity clauses can make you financially responsible for the other party's losses or legal fees, often beyond your insurance limits.",
  options: ['Yes', 'I am not sure', 'No'],
  scores: [40, 30, 100]
}, {
  id: 'b_fin_3',
  text: 'Do you regularly sign commercial obligations or loans without legal review?',
  context: 'Unreviewed contracts often contain unfavorable terms, hidden fees, or restrictive covenants that can harm your business.',
  options: ['Yes, frequently', 'Sometimes', 'No, we review everything'],
  scores: [0, 50, 100]
}], [{
  id: 'b_con_1',
  text: 'Does your business rely on contracts that are reused without legal review?',
  context: "Reusing old or generic contracts can leave you exposed to outdated laws or terms that don't fit your current services.",
  options: ['Yes', 'Sometimes', 'No, we use updated templates'],
  scores: [0, 50, 100]
}, {
  id: 'b_con_2',
  text: 'Do you have written agreements with all key suppliers, vendors, and independent contractors?',
  context: 'Written agreements clarify pricing, delivery terms, liability, and termination rights. Without them, you have little recourse.',
  options: ['Yes', 'Most of them', 'No'],
  scores: [100, 60, 0]
}, {
  id: 'b_con_3',
  text: 'Have you registered trademarks for your brand name or logo with the Canadian Intellectual Property Office (CIPO)?',
  context: 'Trademark registration gives you exclusive rights to your brand across Canada and makes it easier to stop copycats.',
  options: ['Yes', 'Pending', 'No', 'Not applicable'],
  scores: [100, 70, 0, 100]
}], [{
  id: 'b_hr_1',
  text: 'Do all employees have signed employment contracts that comply with provincial Employment Standards?',
  context: 'Employment contracts clarify compensation, duties, and termination notice. Without them, you may owe more severance than expected.',
  options: ['Yes', 'Some do', 'No', 'No employees'],
  scores: [100, 50, 0, 100]
}, {
  id: 'b_hr_2',
  text: 'Do you have an up-to-date employee handbook including a Workplace Violence and Harassment Policy?',
  context: 'Provincial occupational health and safety laws require policies on workplace violence and harassment.',
  options: ['Yes', 'Needs updating', 'No', 'No employees'],
  scores: [100, 50, 0, 100]
}, {
  id: 'b_hr_3',
  text: 'Are you confident your independent contractors are properly classified to avoid CRA misclassification risks?',
  context: 'The CRA can reclassify contractors as employees retroactively, resulting in back taxes, CPP/EI premiums, penalties, and interest.',
  options: ['Yes', 'Not sure', 'No', 'No contractors'],
  scores: [100, 40, 0, 100]
}], [{
  id: 'b_priv_1',
  text: 'Do you collect personal information through your website or business operations?',
  context: 'Collecting personal data triggers obligations under PIPEDA and provincial privacy laws.',
  options: ['Yes', 'No'],
  scores: [50, 100]
}, {
  id: 'b_priv_2',
  text: 'Does your website have a compliant Privacy Policy and Terms of Use?',
  context: 'PIPEDA requires businesses that collect personal information to have a publicly available privacy policy.',
  options: ['Yes', 'No', 'No website'],
  scores: [100, 0, 100]
}, {
  id: 'b_priv_3',
  text: 'Do you have appropriate commercial general liability (CGL) and professional liability (E&O) insurance?',
  context: 'CGL protects against bodily injury/property damage claims. E&O covers professional mistakes. Without them, a claim could bankrupt you.',
  options: ['Yes', 'No', 'Not sure if coverage is adequate'],
  scores: [100, 0, 40]
}], [{
  id: 'b_read_1',
  text: 'Are your business legal records organized and easily accessible?',
  context: 'Disorganized records can delay financing, complicate audits, and increase legal fees during due diligence.',
  options: ['Yes', 'Somewhat', 'No'],
  scores: [100, 50, 0]
}, {
  id: 'b_read_2',
  text: 'Does your business have ongoing legal support for day-to-day questions?',
  context: 'Having a legal partner helps you catch small issues before they become expensive problems.',
  options: ['Yes', 'Occasionally', 'No'],
  scores: [100, 50, 0]
}, {
  id: 'b_read_3',
  text: 'Are important compliance deadlines and renewals (e.g., leases, licenses, corporate filings) being tracked?',
  context: 'Missing a renewal deadline can result in lost leases, lapsed licenses, or corporate dissolution.',
  options: ['Yes, systematically', 'Somewhat', 'No'],
  scores: [100, 50, 0]
}]];
// Helper function to filter questions based on setup data
function filterQuestions(questions: any[][], setupData: any, type: AssessmentType): any[][] {
  if (type === 'personal') {
    return questions.map(sectionQuestions => {
      return sectionQuestions.filter(q => {
        // Family branching
        if (q.id === 'p_fam_2' && setupData.relationship !== 'common-law' && setupData.relationship !== 'married') return false;
        if (q.id === 'p_fam_3' && setupData.relationship !== 'separated' && setupData.relationship !== 'divorced') return false;
        if (q.id === 'p_fam_4' && setupData.children === 'no') return false;
        // Property branching
        if (q.id === 'p_prop_1' && setupData.property === 'no') return false;
        if (q.id === 'p_prop_2' && setupData.property === 'no') return false;
        if (q.id === 'p_prop_3' && setupData.landlord === 'yes') return false; // if they are landlord, they might not be renting, but let's just use property='no' for tenants
        if (q.id === 'p_prop_3' && setupData.property === 'yes') return false; // owners don't need lease
        if (q.id === 'p_prop_4' && setupData.landlord === 'no') return false;
        // Special branching
        if (q.id === 'p_spec_1' && setupData.leavingCanada === 'no') return false;
        return true;
      });
    });
  } else {
    return questions.map(sectionQuestions => {
      return sectionQuestions.filter(q => {
        // Setup branching
        if (q.id === 'b_set_2' && setupData.businessType === 'Sole Proprietorship') return false;
        if (q.id === 'b_set_3' && setupData.businessType === 'Sole Proprietorship') return false;
        // Contracts branching
        if (q.id === 'b_con_3' && setupData.createsIP === 'no') return false;
        // HR branching
        if ((q.id === 'b_hr_1' || q.id === 'b_hr_2') && setupData.employees === 'Just me (0)') return false;
        // Privacy branching
        if (q.id === 'b_priv_2' && setupData.hasWebsite === 'no') return false;
        return true;
      });
    });
  }
}
// Helper function to calculate scores from answers
function calculateScores(answers: Record<string, string>, type: AssessmentType, setupData: any): {
  overall: number;
  sections: number[];
} {
  const allQuestions = type === 'personal' ? PERSONAL_QUESTIONS : BUSINESS_QUESTIONS;
  const filteredQuestions = filterQuestions(allQuestions, setupData, type);
  const sectionScores: number[] = [];
  filteredQuestions.forEach(sectionQuestions => {
    if (sectionQuestions.length === 0) {
      sectionScores.push(100); // Empty section = perfect score
      return;
    }
    let sectionTotal = 0;
    let questionCount = 0;
    sectionQuestions.forEach(q => {
      const answer = answers[q.id];
      if (answer !== undefined) {
        const optionIndex = q.options.indexOf(answer);
        if (optionIndex !== -1 && q.scores) {
          sectionTotal += q.scores[optionIndex];
          questionCount++;
        }
      }
    });
    const sectionScore = questionCount > 0 ? Math.round(sectionTotal / questionCount) : 0;
    sectionScores.push(sectionScore);
  });
  // Calculate overall as average of section scores
  const overall = sectionScores.length > 0 ? Math.round(sectionScores.reduce((a, b) => a + b, 0) / sectionScores.length) : 0;
  return {
    overall,
    sections: sectionScores
  };
}
// Helper function to generate dynamic action plan from answers
function generateDynamicActionPlan(answers: Record<string, string>, type: AssessmentType, setupData: any): {
  id: string;
  text: string;
  completed: boolean;
  category: string;
  priority: 'high' | 'medium' | 'low';
  actionUrl: string;
  actionText: string;
  effort: 'Low' | 'Medium' | 'High';
  time: string;
}[] {
  const items: any[] = [];
  let idCounter = 1;
  if (type === 'personal') {
    // Family
    if (answers.p_fam_1 === 'Yes, recently changed' || answers.p_fam_1 === 'Anticipating a change soon') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review legal documents due to recent/upcoming family changes',
        completed: false,
        category: 'Family',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    if (answers.p_fam_2 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Draft a Cohabitation or Prenuptial Agreement',
        completed: false,
        category: 'Family',
        priority: 'medium',
        actionUrl: '/all-templates',
        actionText: 'Find Template',
        effort: 'High',
        time: '2-3 hours'
      });
    }
    if (answers.p_fam_3 === 'No' || answers.p_fam_3 === 'In progress') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Finalize your Separation Agreement',
        completed: false,
        category: 'Family',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Get Legal Help',
        effort: 'High',
        time: '3-5 hours'
      });
    }
    if (answers.p_fam_4 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Document guardianship wishes for minor children',
        completed: false,
        category: 'Family',
        priority: 'high',
        actionUrl: '/wills',
        actionText: 'Update Will',
        effort: 'Low',
        time: '30 mins'
      });
    }
    // Estate
    if (answers.p_est_1 === 'No, not yet') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Draft your Last Will and Testament',
        completed: false,
        category: 'Estate Planning',
        priority: 'high',
        actionUrl: '/wills',
        actionText: 'Create Will',
        effort: 'Medium',
        time: '1-2 hours'
      });
    } else if (answers.p_est_1 === 'Yes, but it needs updating') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Update your Will to reflect current wishes',
        completed: false,
        category: 'Estate Planning',
        priority: 'medium',
        actionUrl: '/wills',
        actionText: 'Update Will',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    if (answers.p_est_2 === 'No' || answers.p_est_2 === 'I am not sure') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Create a Power of Attorney for Property',
        completed: false,
        category: 'Estate Planning',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Create POA',
        effort: 'Low',
        time: '30 mins'
      });
    }
    if (answers.p_est_3 === 'No' || answers.p_est_3 === 'I am not sure') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Create a Power of Attorney for Personal Care',
        completed: false,
        category: 'Estate Planning',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Create POA',
        effort: 'Low',
        time: '30 mins'
      });
    }
    if (answers.p_est_4 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Document instructions for digital assets',
        completed: false,
        category: 'Estate Planning',
        priority: 'low',
        actionUrl: '/ai-copilot',
        actionText: 'Ask AI Copilot',
        effort: 'Low',
        time: '30 mins'
      });
    }
    // Property
    if (answers.p_prop_2 === 'Yes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review real estate transaction documents',
        completed: false,
        category: 'Property',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'Medium',
        time: '1-2 hours'
      });
    }
    if (answers.p_prop_3 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Obtain a written lease agreement from your landlord',
        completed: false,
        category: 'Property',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Get Advice',
        effort: 'Low',
        time: '30 mins'
      });
    }
    if (answers.p_prop_4 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Use standardized lease agreements for your tenants',
        completed: false,
        category: 'Property',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Find Lease Template',
        effort: 'Low',
        time: '30 mins'
      });
    }
    // Work & Money
    if (answers.p_work_1 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Request a written employment contract',
        completed: false,
        category: 'Work & Money',
        priority: 'low',
        actionUrl: '/ai-copilot',
        actionText: 'Ask AI Copilot',
        effort: 'Low',
        time: '15 mins'
      });
    }
    if (answers.p_work_2 === 'Yes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Explore legal options for debt management',
        completed: false,
        category: 'Work & Money',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Get Legal Help',
        effort: 'High',
        time: '2 hours'
      });
    }
    if (answers.p_work_3 === 'Yes, currently involved' || answers.p_work_3 === 'Anticipating one') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Seek early legal intervention for your dispute',
        completed: false,
        category: 'Work & Money',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'High',
        time: '2 hours'
      });
    }
    // Special
    if (answers.p_spec_1 === 'Yes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review immigration sponsorship obligations',
        completed: false,
        category: 'Special Situations',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Get Advice',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    if (answers.p_spec_2 === 'Yes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review cross-border asset planning',
        completed: false,
        category: 'Special Situations',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'High',
        time: '2 hours'
      });
    }
    if (answers.p_spec_3 === 'Yes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Set up special needs trust or guardianship planning',
        completed: false,
        category: 'Special Situations',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'High',
        time: '3 hours'
      });
    }
    // Readiness
    if (answers.p_read_1 === 'No' || answers.p_read_1 === 'Some of them') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Organize legal documents in a secure location',
        completed: false,
        category: 'Readiness',
        priority: 'medium',
        actionUrl: '/my-documents',
        actionText: 'Upload to Hub',
        effort: 'Low',
        time: '1 hour'
      });
    }
    if (answers.p_read_2 === 'No' || answers.p_read_2 === 'They have a general idea') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Share document locations with emergency contacts',
        completed: false,
        category: 'Readiness',
        priority: 'medium',
        actionUrl: '/my-account',
        actionText: 'Update Contacts',
        effort: 'Low',
        time: '15 mins'
      });
    }
  } else {
    // Business
    // Setup
    if (answers.b_set_1 === 'No, sole proprietorship' || answers.b_set_1 === 'No, general partnership') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Consider incorporating your business',
        completed: false,
        category: 'Setup',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'High',
        time: '3-5 hours'
      });
    }
    if (answers.b_set_2 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Draft a Shareholders or Partnership Agreement',
        completed: false,
        category: 'Setup',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Find Template',
        effort: 'High',
        time: '2-4 hours'
      });
    } else if (answers.b_set_2 === 'Yes, but it is informal') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Formalize your Partnership Agreement',
        completed: false,
        category: 'Setup',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Get Legal Review',
        effort: 'Medium',
        time: '1-2 hours'
      });
    }
    if (answers.b_set_3 === 'No, we are behind' || answers.b_set_3 === 'I do not know what that is') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Update corporate minute books and file annual returns',
        completed: false,
        category: 'Setup',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'Medium',
        time: '1-2 hours'
      });
    }
    // Finance
    if (answers.b_fin_1 === 'Yes, and I signed it' || answers.b_fin_1 === 'Yes, but I am reviewing it') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review personal guarantee exposure',
        completed: false,
        category: 'Finance',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Get Advice',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    if (answers.b_fin_2 === 'Yes' || answers.b_fin_2 === 'I am not sure') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review indemnity clauses in your contracts',
        completed: false,
        category: 'Finance',
        priority: 'medium',
        actionUrl: '/ai-copilot',
        actionText: 'Ask AI Copilot',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    if (answers.b_fin_3 === 'Yes, frequently' || answers.b_fin_3 === 'Sometimes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Establish a legal review process for commercial obligations',
        completed: false,
        category: 'Finance',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'Medium',
        time: '2 hours'
      });
    }
    // Contracts
    if (answers.b_con_1 === 'Yes' || answers.b_con_1 === 'Sometimes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Update and standardize client service agreements',
        completed: false,
        category: 'Contracts',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Find Template',
        effort: 'Medium',
        time: '1-2 hours'
      });
    }
    if (answers.b_con_2 === 'Most of them' || answers.b_con_2 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Create written agreements with all key suppliers',
        completed: false,
        category: 'Contracts',
        priority: 'medium',
        actionUrl: '/all-templates',
        actionText: 'View Templates',
        effort: 'Medium',
        time: '1-3 hours'
      });
    }
    if (answers.b_con_3 === 'No' || answers.b_con_3 === 'Pending') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Register trademarks for brand protection',
        completed: false,
        category: 'Contracts & IP',
        priority: 'low',
        actionUrl: '/legal-help',
        actionText: 'Get Guidance',
        effort: 'Medium',
        time: '1-2 hours'
      });
    }
    // HR
    if (answers.b_hr_1 === 'Some do' || answers.b_hr_1 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Ensure all employees have signed employment contracts',
        completed: false,
        category: 'HR',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'View HR Templates',
        effort: 'High',
        time: '2-4 hours'
      });
    }
    if (answers.b_hr_2 === 'Needs updating' || answers.b_hr_2 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Create or update employee handbook',
        completed: false,
        category: 'HR',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Find Policy Templates',
        effort: 'Medium',
        time: '2-3 hours'
      });
    }
    if (answers.b_hr_3 === 'No' || answers.b_hr_3 === 'Not sure') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review contractor classifications',
        completed: false,
        category: 'HR',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Get Expert Advice',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    // Privacy
    if (answers.b_priv_1 === 'Yes' && answers.b_priv_2 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Create a PIPEDA-compliant Privacy Policy',
        completed: false,
        category: 'Privacy',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Create Policy',
        effort: 'Low',
        time: '30 mins'
      });
    }
    if (answers.b_priv_3 === 'No' || answers.b_priv_3 === 'Not sure if coverage is adequate') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review and obtain adequate CGL and E&O insurance',
        completed: false,
        category: 'Privacy & Compliance',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Get Advice',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    // Readiness
    if (answers.b_read_1 === 'No' || answers.b_read_1 === 'Somewhat') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Organize business legal records',
        completed: false,
        category: 'Readiness',
        priority: 'medium',
        actionUrl: '/my-documents',
        actionText: 'Upload to Hub',
        effort: 'Low',
        time: '1-2 hours'
      });
    }
    if (answers.b_read_2 === 'No' || answers.b_read_2 === 'Occasionally') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Establish ongoing legal support',
        completed: false,
        category: 'Readiness',
        priority: 'low',
        actionUrl: '/legal-help',
        actionText: 'Explore Options',
        effort: 'Low',
        time: '30 mins'
      });
    }
    if (answers.b_read_3 === 'No' || answers.b_read_3 === 'Somewhat') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Implement a compliance tracking system',
        completed: false,
        category: 'Readiness',
        priority: 'medium',
        actionUrl: '/ai-copilot',
        actionText: 'Ask AI Copilot',
        effort: 'Low',
        time: '1 hour'
      });
    }
  }
  return items;
}
// Helper function to get section-specific summary based on answers
function getSectionSummary(sectionId: string, answers: Record<string, string>, type: AssessmentType, score: number): string {
  if (type === 'personal') {
    switch (sectionId) {
      case 'family':
        if (answers.p_fam_3 === 'No') return 'You need a Separation Agreement to finalize your separation.';
        if (answers.p_fam_4 === 'No') return 'Guardianship wishes for your children are not documented.';
        if (answers.p_fam_2 === 'No') return 'Consider a Cohabitation Agreement to protect your interests.';
        return 'Your family legal matters are well-documented.';
      case 'estate':
        if (answers.p_est_1 === 'No, not yet') return 'You do not have a Will yet - this is your most critical gap.';
        if (answers.p_est_1 === 'Yes, but it needs updating') return 'Your Will needs updating to reflect your current situation.';
        if (answers.p_est_2 === 'No' || answers.p_est_3 === 'No') return 'Missing Power of Attorney documents for incapacity planning.';
        return 'Your estate planning foundation is solid.';
      case 'property':
        if (answers.p_prop_3 === 'No') return 'You need a written lease to protect your tenant rights.';
        if (answers.p_prop_4 === 'No') return 'You need standardized lease agreements for your tenants.';
        return 'Your property arrangements are properly structured.';
      case 'work_money':
        if (answers.p_work_2 === 'Yes') return 'Major debt issues require immediate legal/financial attention.';
        if (answers.p_work_3 === 'Yes, currently involved') return 'Active disputes require legal intervention.';
        if (answers.p_work_1 === 'No') return 'Missing employment contract - your severance rights are unclear.';
        return 'Your personal risk management is in good shape.';
      case 'special':
        if (answers.p_spec_1 === 'Yes') return 'Immigration sponsorship involves strict legal commitments.';
        if (answers.p_spec_3 === 'Yes') return 'Special needs trusts may be required for your dependants.';
        return 'Special situations are managed effectively.';
      case 'readiness':
        if (answers.p_read_1 === 'No') return 'Your legal documents are not properly stored or accessible.';
        if (answers.p_read_2 === 'No') return 'Emergency contacts do not know where to find your documents.';
        return 'Your documents are well-organized and accessible.';
      default:
        return score >= 70 ? 'Strong Foundation' : score >= 40 ? 'Needs Review' : 'Action Recommended';
    }
  } else {
    switch (sectionId) {
      case 'setup':
        if (answers.b_set_1 === 'No, sole proprietorship' || answers.b_set_1 === 'No, general partnership') return 'Not incorporated - your personal assets are at risk.';
        if (answers.b_set_2 === 'No') return 'Missing Shareholders Agreement - vulnerable to partner disputes.';
        if (answers.b_set_3 !== 'Yes, fully up to date') return 'Corporate records are behind - risking compliance issues.';
        return 'Your corporate structure is properly maintained.';
      case 'finance':
        if (answers.b_fin_1 === 'Yes, and I signed it') return 'Personal guarantees put your personal assets at risk.';
        if (answers.b_fin_3 === 'Yes, frequently') return 'Signing without review exposes you to unfavorable terms.';
        return 'Financial commitments are well managed.';
      case 'contracts':
        if (answers.b_con_1 === 'Yes') return 'Reusing old contracts leaves you exposed to outdated terms.';
        if (answers.b_con_2 === 'No') return 'Missing supplier/contractor agreements - vulnerable to disruption.';
        return 'Your commercial contracts are in good shape.';
      case 'hr':
        if (answers.b_hr_1 === 'No' || answers.b_hr_1 === 'Some do') return 'Missing employee contracts - severance exposure is high.';
        if (answers.b_hr_2 === 'No') return 'No workplace policies - non-compliant with safety regulations.';
        if (answers.b_hr_3 === 'No') return 'Contractor misclassification risk - CRA penalties possible.';
        return 'Your HR practices are compliant and well-documented.';
      case 'privacy':
        if (answers.b_priv_2 === 'No') return 'Missing Privacy Policy - non-compliant with PIPEDA.';
        if (answers.b_priv_3 === 'No') return 'No liability insurance - one lawsuit could bankrupt the business.';
        return 'Your privacy and compliance protections are solid.';
      case 'readiness':
        if (answers.b_read_1 === 'No') return 'Disorganized records can delay financing and complicate audits.';
        if (answers.b_read_3 === 'No') return 'Missing renewal deadlines can halt operations immediately.';
        return 'Your business readiness is strong.';
      default:
        return score >= 70 ? 'Strong Foundation' : score >= 40 ? 'Needs Review' : 'Action Recommended';
    }
  }
}
function AnimatedScore({
  score
}: {
  score: number;
}) {
  const [displayScore, setDisplayScore] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = score;
    if (start === end) {
      setDisplayScore(end);
      return;
    }
    const totalDuration = 1500;
    const incrementTime = totalDuration / (end || 1);
    const timer = setInterval(() => {
      start += 1;
      setDisplayScore(start);
      if (start >= end) clearInterval(timer);
    }, incrementTime);
    return () => clearInterval(timer);
  }, [score]);
  const label = score >= 70 ? 'Strong' : score >= 40 ? 'Review' : 'Action';
  const color = score >= 70 ? 'text-green-500' : score >= 40 ? 'text-amber-500' : 'text-destructive';
  return <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-2295">
      <span className="text-4xl font-bold text-primary" data-id="element-2296">{displayScore}</span>
      <span className={`text-sm font-bold uppercase tracking-wider ${color}`} data-id="element-2297">
        {label}
      </span>
      <span className="text-xs font-medium text-secondary uppercase tracking-wider mt-1" data-id="element-2298">
        / 100
      </span>
    </div>;
}
export function LegalHealthCheck() {
  const navigate = useNavigate();
  const {
    toggleMobileSidebar
  } = useOutletContext<DashboardContext>() || {
    toggleMobileSidebar: () => {}
  };
  const [view, setView] = useState<ViewState>('landing');
  const [type, setType] = useState<AssessmentType>('personal');
  const [direction, setDirection] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  // Setup State
  const [setupData, setSetupData] = useState<any>({
    province: 'Ontario',
    // Personal
    relationship: 'single',
    children: 'no',
    property: 'no',
    landlord: 'no',
    leavingCanada: 'no',
    // Business
    businessName: '',
    businessType: 'Corporation',
    employees: 'Just me (0)',
    hasWebsite: 'yes',
    createsIP: 'yes',
    inDispute: 'no'
  });
  // Wizard State
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showSaveToast, setShowSaveToast] = useState(false);
  // Results State
  const [score, setScore] = useState(0);
  const [sectionScores, setSectionScores] = useState<number[]>([]);
  const [actionPlanItems, setActionPlanItems] = useState<{
    id: string;
    text: string;
    completed: boolean;
    category: string;
    priority: 'high' | 'medium' | 'low';
    actionUrl: string;
    actionText: string;
    effort: 'Low' | 'Medium' | 'High';
    time: string;
  }[]>([]);
  // History State
  const [historyItems, setHistoryItems] = useState([{
    id: '1',
    type: 'personal',
    date: 'Oct 12, 2025',
    status: 'completed',
    score: 62,
    topFlag: 'Estate Planning'
  }, {
    id: '2',
    type: 'business',
    date: 'Today',
    status: 'draft',
    score: 40,
    topFlag: 'Contracts'
  }]);
  // Modal States
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  // Toast State
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };
  const handleNavigateToAICopilot = () => navigate('/ai-copilot');
  const handleNavigate = (path: string) => navigate(path);
  const startSetup = (assessmentType: AssessmentType) => {
    setType(assessmentType);
    setView('setup');
    setCurrentSectionIndex(0);
    setAnswers({});
  };
  const startWizard = () => {
    setView('wizard');
  };
  const saveDraft = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setShowSaveToast(true);
      setTimeout(() => setShowSaveToast(false), 3000);
      const newHistory = [...historyItems];
      const existingDraftIndex = newHistory.findIndex(h => h.status === 'draft' && h.type === type);
      const currentScore = Math.round(currentSectionIndex / (type === 'personal' ? PERSONAL_SECTIONS.length : BUSINESS_SECTIONS.length) * 100);
      if (existingDraftIndex >= 0) {
        newHistory[existingDraftIndex].date = 'Just now';
        newHistory[existingDraftIndex].score = currentScore;
      } else {
        newHistory.unshift({
          id: Date.now().toString(),
          type,
          date: 'Just now',
          status: 'draft',
          score: currentScore,
          topFlag: 'In Progress'
        });
      }
      setHistoryItems(newHistory);
    }, 800);
  };
  const generateActionPlan = () => {
    const scores = calculateScores(answers, type, setupData);
    setScore(scores.overall);
    setSectionScores(scores.sections);
    const items = generateDynamicActionPlan(answers, type, setupData);
    setActionPlanItems(items);
  };
  const nextSection = () => {
    const sections = type === 'personal' ? PERSONAL_SECTIONS : BUSINESS_SECTIONS;
    if (currentSectionIndex < sections.length - 1) {
      setDirection(1);
      setCurrentSectionIndex(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      generateActionPlan();
      setView('results');
      window.scrollTo(0, 0);
      const newHistory = [...historyItems];
      const draftIndex = newHistory.findIndex(h => h.status === 'draft' && h.type === type);
      const scores = calculateScores(answers, type, setupData);
      // Find weakest category for top flag
      const weakestIdx = scores.sections.indexOf(Math.min(...scores.sections));
      const topFlag = sections[weakestIdx]?.title || 'General';
      if (draftIndex >= 0) {
        newHistory[draftIndex] = {
          ...newHistory[draftIndex],
          status: 'completed',
          score: scores.overall,
          date: 'Just now',
          topFlag
        };
      } else {
        newHistory.unshift({
          id: Date.now().toString(),
          type,
          date: 'Just now',
          status: 'completed',
          score: scores.overall,
          topFlag
        });
      }
      setHistoryItems(newHistory);
    }
  };
  const prevSection = () => {
    if (currentSectionIndex > 0) {
      setDirection(-1);
      setCurrentSectionIndex(prev => prev - 1);
      window.scrollTo(0, 0);
    } else {
      setView('setup');
    }
  };
  const handleAnswer = (questionId: string, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };
  const toggleActionItem = (id: string) => {
    setActionPlanItems(prev => prev.map(item => item.id === id ? {
      ...item,
      completed: !item.completed
    } : item));
  };
  // Dynamic Theme Classes based on Assessment Type
  const themeBg = type === 'personal' ? 'bg-violetBlue' : 'bg-turquoise';
  const themeText = type === 'personal' ? 'text-violetBlue' : 'text-turquoise';
  const themeBorder = type === 'personal' ? 'border-violetBlue' : 'border-turquoise';
  const themeHoverBg = type === 'personal' ? 'hover:bg-[#6844E8]' : 'hover:bg-turquoise/90';
  const themeLightBg = type === 'personal' ? 'bg-aliceBlue' : 'bg-turquoise/10';
  const themeBorderLight = type === 'personal' ? 'border-violetBlue/20' : 'border-turquoise/20';
  const renderLanding = () => <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-2299">
      <div className="text-center mb-12" data-id="element-2300">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-aliceBlue rounded-full mb-6" data-id="element-2301">
          <Activity className="w-8 h-8 text-violetBlue" data-id="element-2302" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-primary mb-3" data-id="element-2303">
          Annual Legal Wellness Check
        </h1>

        <p className="text-sm text-secondary max-w-xl mx-auto italic" data-id="element-2304">
          This assessment helps surface common legal issues worth reviewing. It
          is for informational purposes only and does not replace legal advice.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-16" data-id="element-2305">
        {/* Personal Card */}
        <div className="bg-white border border-borderColor rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-2306">
          <div className="flex items-start gap-4 mb-6" data-id="element-2307">
            <div className="p-3 bg-aliceBlue rounded-xl" data-id="element-2308">
              <Activity className="w-8 h-8 text-violetBlue" data-id="element-2309" />
            </div>
            <div data-id="element-2310">
              <h2 className="text-xl font-bold text-primary mb-1" data-id="element-2311">
                Personal Legal Health Check
              </h2>
              <div className="flex items-center gap-2 text-sm text-secondary" data-id="element-2312">
                <Clock className="w-4 h-4" data-id="element-2313" />
                <span data-id="element-2314">~10 min to complete</span>
              </div>
            </div>
          </div>
          <p className="text-secondary mb-8 flex-grow leading-relaxed" data-id="element-2315">
            Review your family changes, estate planning, property ownership, and
            personal risk. Ensure your loved ones are protected and your
            documents are ready for the unexpected.
          </p>
          <button onClick={() => startSetup('personal')} className="w-full bg-violetBlue hover:bg-[#6844E8] text-white font-medium py-3.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-2316">
            Start Personal Check
            <ChevronRight className="w-5 h-5" data-id="element-2317" />
          </button>
        </div>

        {/* Business Card */}
        <div className="bg-white border border-borderColor rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-2318">
          <div className="flex items-start gap-4 mb-6" data-id="element-2319">
            <div className="p-3 bg-turquoise/10 rounded-xl" data-id="element-2320">
              <Building2 className="w-8 h-8 text-turquoise" data-id="element-2321" />
            </div>
            <div data-id="element-2322">
              <h2 className="text-xl font-bold text-primary mb-1" data-id="element-2323">
                Business Legal Health Check
              </h2>
              <div className="flex items-center gap-2 text-sm text-secondary" data-id="element-2324">
                <Clock className="w-4 h-4" data-id="element-2325" />
                <span data-id="element-2326">~15 min to complete</span>
              </div>
            </div>
          </div>
          <p className="text-secondary mb-8 flex-grow leading-relaxed" data-id="element-2327">
            Review your corporate structure, financial commitments, contracts,
            HR compliance, and privacy policies. Identify hidden liabilities
            before they become costly problems.
          </p>
          <button onClick={() => startSetup('business')} className="w-full bg-turquoise hover:bg-turquoise/90 text-white font-medium py-3.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-2328">
            Start Business Check
            <ChevronRight className="w-5 h-5" data-id="element-2329" />
          </button>
        </div>
      </div>

      {/* How it works & History */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2330">
        <div className="lg:col-span-2" data-id="element-2331">
          <h3 className="text-xl font-bold text-primary mb-6" data-id="element-2332">How it works</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-2333">
            <div className="bg-aliceBlue rounded-xl p-6 relative overflow-hidden" data-id="element-2334">
              <div className="text-6xl font-bold text-white absolute -right-2 -bottom-4 opacity-50 select-none" data-id="element-2335">
                1
              </div>
              <div className="relative z-10" data-id="element-2336">
                <FileText className="w-6 h-6 text-violetBlue mb-4" data-id="element-2337" />
                <h4 className="font-semibold text-primary mb-2" data-id="element-2338">
                  Answer Questions
                </h4>
                <p className="text-sm text-secondary" data-id="element-2339">
                  Complete a guided questionnaire tailored to your situation.
                </p>
              </div>
            </div>
            <div className="bg-aliceBlue rounded-xl p-6 relative overflow-hidden" data-id="element-2340">
              <div className="text-6xl font-bold text-white absolute -right-2 -bottom-4 opacity-50 select-none" data-id="element-2341">
                2
              </div>
              <div className="relative z-10" data-id="element-2342">
                <Activity className="w-6 h-6 text-violetBlue mb-4" data-id="element-2343" />
                <h4 className="font-semibold text-primary mb-2" data-id="element-2344">
                  Get Your Score
                </h4>
                <p className="text-sm text-secondary" data-id="element-2345">
                  Instantly see your legal health score and specific risk areas.
                </p>
              </div>
            </div>
            <div className="bg-aliceBlue rounded-xl p-6 relative overflow-hidden" data-id="element-2346">
              <div className="text-6xl font-bold text-white absolute -right-2 -bottom-4 opacity-50 select-none" data-id="element-2347">
                3
              </div>
              <div className="relative z-10" data-id="element-2348">
                <CheckCircle className="w-6 h-6 text-violetBlue mb-4" data-id="element-2349" />
                <h4 className="font-semibold text-primary mb-2" data-id="element-2350">Take Action</h4>
                <p className="text-sm text-secondary" data-id="element-2351">
                  Receive a personalized plan with prioritized next steps.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1" data-id="element-2352">
          <div className="flex items-center justify-between mb-6" data-id="element-2353">
            <h3 className="text-xl font-bold text-primary" data-id="element-2354">
              Saved Assessments
            </h3>
            <button onClick={() => setView('history')} className="text-sm text-violetBlue hover:underline font-medium" data-id="element-2355">
              View All
            </button>
          </div>
          <div className="bg-white border border-borderColor rounded-xl shadow-sm overflow-hidden" data-id="element-2356">
            {historyItems.slice(0, 3).map(item => <div key={item.id} className="p-4 border-b border-borderColor hover:bg-aliceBlue transition-colors cursor-pointer last:border-b-0" onClick={() => {
            setType(item.type as AssessmentType);
            if (item.status === 'completed') {
              generateActionPlan();
              setView('results');
            } else {
              startSetup(item.type as AssessmentType);
            }
          }} data-id="element-2357">
                <div className="flex items-center justify-between mb-2" data-id="element-2358">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${item.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-2359">
                    {item.status === 'completed' ? 'Completed' : 'Draft'}
                  </span>
                  <span className="text-xs text-secondary" data-id="element-2360">{item.date}</span>
                </div>
                <h4 className="font-semibold text-primary text-sm mb-1 capitalize" data-id="element-2361">
                  {item.type} Health Check
                </h4>
                <div className="flex items-center gap-2" data-id="element-2362">
                  <div className="w-full bg-borderColor rounded-full h-1.5" data-id="element-2363">
                    <div className={`${item.status === 'completed' ? item.score >= 70 ? 'bg-green-500' : item.score >= 40 ? 'bg-amber-500' : 'bg-destructive' : 'bg-violetBlue'} h-1.5 rounded-full`} style={{
                  width: `${item.score}%`
                }} data-id="element-2364"></div>
                  </div>
                  <span className="text-xs font-medium text-primary" data-id="element-2365">
                    {item.status === 'completed' ? `${item.score}/100` : `${item.score}%`}
                  </span>
                </div>
              </div>)}
            {historyItems.length === 0 && <div className="p-6 text-center text-secondary text-sm" data-id="element-2366">
                No assessments saved yet.
              </div>}
          </div>
        </div>
      </div>
    </div>;
  const renderSetup = () => <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-2367">
      <button onClick={() => setView('landing')} className="flex items-center gap-2 text-secondary hover:text-primary transition-colors mb-8 text-sm font-medium" data-id="element-2368">
        <ArrowLeft className="w-4 h-4" data-id="element-2369" />
        Back to Overview
      </button>

      <div className="bg-white border border-borderColor rounded-2xl p-6 sm:p-10 shadow-sm" data-id="element-2370">
        <div className="flex items-center gap-4 mb-6" data-id="element-2371">
          <div className={`p-3 rounded-xl ${themeLightBg}`} data-id="element-2372">
            {type === 'personal' ? <Activity className="w-8 h-8 text-violetBlue" data-id="element-2373" /> : <Building2 className="w-8 h-8 text-turquoise" data-id="element-2374" />}
          </div>
          <div data-id="element-2375">
            <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2" data-id="element-2376">
              {type === 'personal' ? 'Personal' : 'Business'} Legal Health Check
            </h1>
            <div className="flex items-center gap-4 text-sm text-secondary" data-id="element-2377">
              <span className="flex items-center gap-1" data-id="element-2378">
                <Clock className="w-4 h-4" data-id="element-2379" /> ~
                {type === 'personal' ? '10' : '15'} min
              </span>
              <span className="flex items-center gap-1" data-id="element-2380">
                <Save className="w-4 h-4" data-id="element-2381" /> Auto-saves progress
              </span>
            </div>
          </div>
        </div>

        <div className={`bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg mb-8`} data-id="element-2382">
          <div className="flex items-start gap-3" data-id="element-2383">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-2384" />
            <div data-id="element-2385">
              <h4 className="font-bold text-primary mb-2 text-sm" data-id="element-2386">
                Important Legal Disclaimer
              </h4>
              <p className="text-sm text-secondary leading-relaxed" data-id="element-2387">
                This assessment is designed for general informational purposes
                under Canadian law. It does not constitute legal advice and
                should not be relied upon as a substitute for consultation with
                a qualified lawyer licensed in your province. Your answers are
                secure and confidential.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6 mb-10" data-id="element-2388">
          <h3 className="text-lg font-semibold text-primary border-b border-borderColor pb-2" data-id="element-2389">
            Let's get some basic context to tailor your questions
          </h3>

          <div data-id="element-2390">
            <label className="block text-sm font-medium text-primary mb-2" data-id="element-2391">
              Which province or territory are you located in?
            </label>
            <select className={`w-full border border-borderColor rounded-lg px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-opacity-20 focus:border-opacity-100 bg-white ${type === 'personal' ? 'focus:ring-violetBlue focus:border-violetBlue' : 'focus:ring-turquoise focus:border-turquoise'}`} value={setupData.province} onChange={e => setSetupData({
            ...setupData,
            province: e.target.value
          })} data-id="element-2392">
              <option value="Ontario" data-id="element-2393">Ontario</option>
              <option value="British Columbia" data-id="element-2394">British Columbia</option>
              <option value="Alberta" data-id="element-2395">Alberta</option>
              <option value="Quebec" data-id="element-2396">Quebec</option>
              <option value="Manitoba" data-id="element-2397">Manitoba</option>
              <option value="Saskatchewan" data-id="element-2398">Saskatchewan</option>
              <option value="Nova Scotia" data-id="element-2399">Nova Scotia</option>
              <option value="New Brunswick" data-id="element-2400">New Brunswick</option>
              <option value="Newfoundland and Labrador" data-id="element-2401">
                Newfoundland and Labrador
              </option>
              <option value="Prince Edward Island" data-id="element-2402">Prince Edward Island</option>
              <option value="Northwest Territories" data-id="element-2403">
                Northwest Territories
              </option>
              <option value="Nunavut" data-id="element-2404">Nunavut</option>
              <option value="Yukon" data-id="element-2405">Yukon</option>
            </select>
          </div>

          {type === 'personal' ? <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2406">
                <div data-id="element-2407">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-2408">
                    Relationship Status
                  </label>
                  <select className="w-full border border-borderColor rounded-lg px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-violetBlue/20 focus:border-violetBlue bg-white" value={setupData.relationship} onChange={e => setSetupData({
                ...setupData,
                relationship: e.target.value
              })} data-id="element-2409">
                    <option value="single" data-id="element-2410">Single</option>
                    <option value="married" data-id="element-2411">Married</option>
                    <option value="common-law" data-id="element-2412">Common-law</option>
                    <option value="separated" data-id="element-2413">Separated</option>
                    <option value="divorced" data-id="element-2414">Divorced</option>
                    <option value="widowed" data-id="element-2415">Widowed</option>
                  </select>
                </div>
                <div data-id="element-2416">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-2417">
                    Do you have children?
                  </label>
                  <div className="flex gap-4" data-id="element-2418">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2419">
                      <input type="radio" name="children" value="yes" checked={setupData.children === 'yes'} onChange={e => setSetupData({
                    ...setupData,
                    children: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-2420" />
                      <span className="text-sm text-primary" data-id="element-2421">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2422">
                      <input type="radio" name="children" value="no" checked={setupData.children === 'no'} onChange={e => setSetupData({
                    ...setupData,
                    children: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-2423" />
                      <span className="text-sm text-primary" data-id="element-2424">No</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2425">
                <div data-id="element-2426">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-2427">
                    Do you own property?
                  </label>
                  <div className="flex gap-4" data-id="element-2428">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2429">
                      <input type="radio" name="property" value="yes" checked={setupData.property === 'yes'} onChange={e => setSetupData({
                    ...setupData,
                    property: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-2430" />
                      <span className="text-sm text-primary" data-id="element-2431">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2432">
                      <input type="radio" name="property" value="no" checked={setupData.property === 'no'} onChange={e => setSetupData({
                    ...setupData,
                    property: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-2433" />
                      <span className="text-sm text-primary" data-id="element-2434">No</span>
                    </label>
                  </div>
                </div>
                <div data-id="element-2435">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-2436">
                    Are you a landlord?
                  </label>
                  <div className="flex gap-4" data-id="element-2437">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2438">
                      <input type="radio" name="landlord" value="yes" checked={setupData.landlord === 'yes'} onChange={e => setSetupData({
                    ...setupData,
                    landlord: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-2439" />
                      <span className="text-sm text-primary" data-id="element-2440">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2441">
                      <input type="radio" name="landlord" value="no" checked={setupData.landlord === 'no'} onChange={e => setSetupData({
                    ...setupData,
                    landlord: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-2442" />
                      <span className="text-sm text-primary" data-id="element-2443">No</span>
                    </label>
                  </div>
                </div>
              </div>

              <div data-id="element-2444">
                <label className="block text-sm font-medium text-primary mb-3" data-id="element-2445">
                  Are you planning to leave Canada or sponsor someone to come to
                  Canada?
                </label>
                <div className="flex gap-4" data-id="element-2446">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-2447">
                    <input type="radio" name="leavingCanada" value="yes" checked={setupData.leavingCanada === 'yes'} onChange={e => setSetupData({
                  ...setupData,
                  leavingCanada: e.target.value
                })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-2448" />
                    <span className="text-sm text-primary" data-id="element-2449">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-2450">
                    <input type="radio" name="leavingCanada" value="no" checked={setupData.leavingCanada === 'no'} onChange={e => setSetupData({
                  ...setupData,
                  leavingCanada: e.target.value
                })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-2451" />
                    <span className="text-sm text-primary" data-id="element-2452">No</span>
                  </label>
                </div>
              </div>
            </> : <>
              <div data-id="element-2453">
                <label className="block text-sm font-medium text-primary mb-2" data-id="element-2454">
                  Business Name
                </label>
                <input type="text" value={setupData.businessName} onChange={e => setSetupData({
              ...setupData,
              businessName: e.target.value
            })} placeholder="Enter your business name" className="w-full border border-borderColor rounded-lg px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise" data-id="element-2455" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2456">
                <div data-id="element-2457">
                  <label className="block text-sm font-medium text-primary mb-2" data-id="element-2458">
                    Business Type
                  </label>
                  <select value={setupData.businessType} onChange={e => setSetupData({
                ...setupData,
                businessType: e.target.value
              })} className="w-full border border-borderColor rounded-lg px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise bg-white" data-id="element-2459">
                    <option data-id="element-2460">Corporation</option>
                    <option data-id="element-2461">Sole Proprietorship</option>
                    <option data-id="element-2462">Partnership</option>
                    <option data-id="element-2463">Other</option>
                  </select>
                </div>
                <div data-id="element-2464">
                  <label className="block text-sm font-medium text-primary mb-2" data-id="element-2465">
                    Number of Employees
                  </label>
                  <select value={setupData.employees} onChange={e => setSetupData({
                ...setupData,
                employees: e.target.value
              })} className="w-full border border-borderColor rounded-lg px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise bg-white" data-id="element-2466">
                    <option data-id="element-2467">Just me (0)</option>
                    <option data-id="element-2468">1-10</option>
                    <option data-id="element-2469">11-50</option>
                    <option data-id="element-2470">51+</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2471">
                <div data-id="element-2472">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-2473">
                    Does your business have a website?
                  </label>
                  <div className="flex gap-4" data-id="element-2474">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2475">
                      <input type="radio" name="hasWebsite" value="yes" checked={setupData.hasWebsite === 'yes'} onChange={e => setSetupData({
                    ...setupData,
                    hasWebsite: e.target.value
                  })} className="w-4 h-4 text-turquoise focus:ring-turquoise" data-id="element-2476" />
                      <span className="text-sm text-primary" data-id="element-2477">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2478">
                      <input type="radio" name="hasWebsite" value="no" checked={setupData.hasWebsite === 'no'} onChange={e => setSetupData({
                    ...setupData,
                    hasWebsite: e.target.value
                  })} className="w-4 h-4 text-turquoise focus:ring-turquoise" data-id="element-2479" />
                      <span className="text-sm text-primary" data-id="element-2480">No</span>
                    </label>
                  </div>
                </div>
                <div data-id="element-2481">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-2482">
                    Do you create original IP or brands?
                  </label>
                  <div className="flex gap-4" data-id="element-2483">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2484">
                      <input type="radio" name="createsIP" value="yes" checked={setupData.createsIP === 'yes'} onChange={e => setSetupData({
                    ...setupData,
                    createsIP: e.target.value
                  })} className="w-4 h-4 text-turquoise focus:ring-turquoise" data-id="element-2485" />
                      <span className="text-sm text-primary" data-id="element-2486">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-2487">
                      <input type="radio" name="createsIP" value="no" checked={setupData.createsIP === 'no'} onChange={e => setSetupData({
                    ...setupData,
                    createsIP: e.target.value
                  })} className="w-4 h-4 text-turquoise focus:ring-turquoise" data-id="element-2488" />
                      <span className="text-sm text-primary" data-id="element-2489">No</span>
                    </label>
                  </div>
                </div>
              </div>
            </>}
        </div>

        <div className="flex justify-end pt-6 border-t border-borderColor" data-id="element-2490">
          <button onClick={startWizard} className={`${themeBg} ${themeHoverBg} text-white font-medium py-3 px-8 rounded-xl transition-colors flex items-center gap-2`} data-id="element-2491">
            Begin Assessment
            <Play className="w-4 h-4 fill-current" data-id="element-2492" />
          </button>
        </div>
      </div>
    </div>;
  const renderWizard = () => {
    const sections = type === 'personal' ? PERSONAL_SECTIONS : BUSINESS_SECTIONS;
    const allQuestions = type === 'personal' ? PERSONAL_QUESTIONS : BUSINESS_QUESTIONS;
    const filteredQuestions = filterQuestions(allQuestions, setupData, type);
    const currentSection = sections[currentSectionIndex];
    const currentQuestions = filteredQuestions[currentSectionIndex];
    const progress = Math.round(currentSectionIndex / sections.length * 100);
    const isSectionComplete = currentQuestions.every(q => answers[q.id] !== undefined);
    return <div className="flex flex-col h-[calc(100vh-64px)] bg-gray-50" data-id="element-2493">
        {/* Wizard Header / Progress */}
        <div className="bg-white border-b border-borderColor px-4 sm:px-6 py-4 flex items-center justify-between z-10" data-id="element-2494">
          <div className="flex items-center gap-4" data-id="element-2495">
            <button onClick={() => setView('setup')} className="p-2 hover:bg-aliceBlue rounded-lg transition-colors text-secondary" data-id="element-2496">
              <ArrowLeft className="w-5 h-5" data-id="element-2497" />
            </button>
            <div data-id="element-2498">
              <h2 className="text-sm font-bold text-primary hidden sm:block" data-id="element-2499">
                {type === 'personal' ? 'Personal' : 'Business'} Legal Health
                Check
              </h2>
              <div className="flex items-center gap-2 mt-1" data-id="element-2500">
                <div className="w-32 sm:w-48 h-2 bg-borderColor rounded-full overflow-hidden" data-id="element-2501">
                  <motion.div className={`h-full ${themeBg}`} initial={{
                  width: 0
                }} animate={{
                  width: `${progress}%`
                }} transition={{
                  duration: 0.5
                }} data-id="element-2502" />
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${themeLightBg} ${themeText}`} data-id="element-2503">
                  {progress}%
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4" data-id="element-2504">
            <div className="hidden sm:flex items-center gap-2 text-sm font-medium text-secondary" data-id="element-2505">
              {isSaving ? <>
                  <Loader2 className={`w-4 h-4 animate-spin ${themeText}`} data-id="element-2506" />
                  <span className={themeText} data-id="element-2507">Saving...</span>
                </> : <>
                  <CheckCircle className="w-4 h-4 text-green-500" data-id="element-2508" />
                  <span data-id="element-2509">Saved</span>
                </>}
            </div>
            <button onClick={saveDraft} disabled={isSaving} className="hidden sm:flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors disabled:opacity-50" data-id="element-2510">
              <Save className="w-4 h-4" data-id="element-2511" />
              Save Draft
            </button>
            <div className="w-px h-4 bg-borderColor hidden sm:block" data-id="element-2512"></div>
            <button onClick={() => setView('landing')} className="text-sm font-medium text-secondary hover:text-primary transition-colors" data-id="element-2513">
              Exit
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex" data-id="element-2514">
          {/* Left Sidebar - Navigation */}
          <div className="hidden lg:block w-64 bg-white border-r border-borderColor overflow-y-auto" data-id="element-2515">
            <div className="p-6" data-id="element-2516">
              <h3 className="text-xs font-bold text-secondary uppercase tracking-wider mb-4" data-id="element-2517">
                Sections
              </h3>
              <div className="space-y-1" data-id="element-2518">
                {sections.map((sec, idx) => {
                const isCompleted = idx < currentSectionIndex;
                const isCurrent = idx === currentSectionIndex;
                const Icon = sec.icon;
                return <div key={sec.id} className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isCurrent ? `${themeLightBg} border ${themeBorderLight}` : isCompleted ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50'}`} onClick={() => {
                  if (isCompleted) {
                    setDirection(idx > currentSectionIndex ? 1 : -1);
                    setCurrentSectionIndex(idx);
                  }
                }} data-id="element-2519">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isCompleted ? 'bg-green-500 text-white' : isCurrent ? `${themeBg} text-white` : 'bg-borderColor text-secondary'}`} data-id="element-2520">
                        {isCompleted ? <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 20
                    }} data-id="element-2521">
                            <Check className="w-4 h-4" data-id="element-2522" />
                          </motion.div> : <Icon className="w-4 h-4" data-id="element-2523" />}
                      </div>
                      <span className={`text-sm font-medium ${isCurrent ? themeText : 'text-primary'}`} data-id="element-2524">
                        {sec.title}
                      </span>
                    </div>;
              })}
              </div>
            </div>
          </div>

          {/* Center - Questions */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-8 lg:p-12 overflow-x-hidden" data-id="element-2525">
            <div className="max-w-2xl mx-auto" data-id="element-2526">
              <AnimatePresence mode="wait" custom={direction} data-id="element-2527">
                <motion.div key={currentSectionIndex} custom={direction} initial={d => ({
                opacity: 0,
                x: d * 60
              })} animate={{
                opacity: 1,
                x: 0
              }} exit={d => ({
                opacity: 0,
                x: d * -60
              })} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-2528">
                  <div className="mb-8" data-id="element-2529">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 ${themeLightBg} ${themeText} text-xs font-bold rounded-full mb-4 uppercase tracking-wide`} data-id="element-2530">
                      Section {currentSectionIndex + 1} of {sections.length}
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-2" data-id="element-2531">
                      {currentSection.title}
                    </h2>
                    <p className="text-secondary" data-id="element-2532">
                      Answer a few questions to evaluate your readiness in this
                      area.
                    </p>
                  </div>

                  <div className="space-y-6 mb-12" data-id="element-2533">
                    {currentQuestions.map((q, qIdx) => <div key={q.id} className="bg-white border border-borderColor rounded-xl p-6 shadow-sm" data-id="element-2534">
                        <h4 className="text-base font-semibold text-primary mb-2 leading-relaxed" data-id="element-2535">
                          {q.text}
                        </h4>
                        <p className="text-xs text-secondary mb-4 leading-relaxed italic" data-id="element-2536">
                          {q.context}
                        </p>
                        <div className="space-y-3" data-id="element-2537">
                          {q.options.map((opt, optIdx) => {
                        const isSelected = answers[q.id] === opt;
                        return <label key={optIdx} className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${isSelected ? `${themeBorder} ${themeLightBg}` : 'border-borderColor hover:bg-aliceBlue'}`} data-id="element-2538">
                                <input type="radio" name={q.id} value={opt} checked={isSelected} onChange={() => handleAnswer(q.id, opt)} className={`w-4 h-4 ${themeText} focus:ring-opacity-50 ${type === 'personal' ? 'focus:ring-violetBlue' : 'focus:ring-turquoise'}`} data-id="element-2539" />
                                <span className="ml-3 text-sm font-medium text-primary" data-id="element-2540">
                                  {opt}
                                </span>
                              </label>;
                      })}
                        </div>
                      </div>)}
                    {currentQuestions.length === 0 && <div className="bg-white border border-borderColor rounded-xl p-8 text-center shadow-sm" data-id="element-2541">
                        <CheckCircle className={`w-12 h-12 mx-auto mb-4 ${themeText}`} data-id="element-2542" />
                        <h4 className="text-lg font-bold text-primary mb-2" data-id="element-2543">
                          No questions needed
                        </h4>
                        <p className="text-secondary" data-id="element-2544">
                          Based on your setup answers, this section is not
                          applicable to your situation.
                        </p>
                      </div>}
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-borderColor" data-id="element-2545">
                    <button onClick={prevSection} className="px-6 py-3 text-sm font-medium text-secondary hover:text-primary transition-colors" data-id="element-2546">
                      Back
                    </button>
                    <button onClick={nextSection} disabled={!isSectionComplete} className={`${isSectionComplete ? `${themeBg} ${themeHoverBg} text-white` : 'bg-disabled text-white cursor-not-allowed'} font-medium py-3 px-8 rounded-xl transition-colors flex items-center gap-2 shadow-sm`} data-id="element-2547">
                      {currentSectionIndex === sections.length - 1 ? 'Complete Assessment' : 'Continue'}
                      {currentSectionIndex !== sections.length - 1 && <ChevronRight className="w-4 h-4" data-id="element-2548" />}
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Right Sidebar - Helper */}
          <div className="hidden xl:block w-80 bg-white border-l border-borderColor p-6 overflow-y-auto" data-id="element-2549">
            <div className={`${themeLightBg} rounded-xl p-5 mb-6`} data-id="element-2550">
              <div className="flex items-center gap-2 mb-3" data-id="element-2551">
                <Info className={`w-5 h-5 ${themeText}`} data-id="element-2552" />
                <h4 className="font-bold text-primary" data-id="element-2553">Why this matters</h4>
              </div>
              <p className="text-sm text-secondary leading-relaxed" data-id="element-2554">
                {currentSection.whyItMatters}
              </p>
            </div>

            <div className="bg-amber-50 rounded-xl p-5 mb-6 border border-amber-100" data-id="element-2555">
              <div className="flex items-center gap-2 mb-3" data-id="element-2556">
                <FileWarning className="w-5 h-5 text-amber-600" data-id="element-2557" />
                <h4 className="font-bold text-primary" data-id="element-2558">Commonly overlooked</h4>
              </div>
              <p className="text-sm text-secondary leading-relaxed" data-id="element-2559">
                {currentSection.commonlyOverlooked}
              </p>
            </div>

            <div className="bg-blue-50 rounded-xl p-5 mb-6 border border-blue-100" data-id="element-2560">
              <div className="flex items-center gap-2 mb-3" data-id="element-2561">
                <RefreshCw className="w-5 h-5 text-blue-600" data-id="element-2562" />
                <h4 className="font-bold text-primary" data-id="element-2563">When to review</h4>
              </div>
              <p className="text-sm text-secondary leading-relaxed" data-id="element-2564">
                {currentSection.whenToReview}
              </p>
            </div>

            <div className="border border-borderColor rounded-xl p-5" data-id="element-2565">
              <h4 className="font-bold text-primary mb-4" data-id="element-2566">Your Progress</h4>
              <div className="space-y-4" data-id="element-2567">
                <div className="flex items-center justify-between text-sm" data-id="element-2568">
                  <span className="text-secondary" data-id="element-2569">Sections Completed</span>
                  <span className="font-medium text-primary" data-id="element-2570">
                    {currentSectionIndex} / {sections.length}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm" data-id="element-2571">
                  <span className="text-secondary" data-id="element-2572">Est. Time Remaining</span>
                  <span className="font-medium text-primary" data-id="element-2573">
                    {Math.max(1, (sections.length - currentSectionIndex) * 2)}{' '}
                    mins
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Toast */}
        <AnimatePresence data-id="element-2574">
          {showSaveToast && <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 50
        }} className="fixed bottom-6 right-6 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-xl flex items-center gap-3 z-50" data-id="element-2575">
              <CheckCircle className="w-5 h-5 text-green-400" data-id="element-2576" />
              <span className="text-sm font-medium" data-id="element-2577">
                Draft saved successfully
              </span>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  const renderResults = () => {
    const sections = type === 'personal' ? PERSONAL_SECTIONS : BUSINESS_SECTIONS;
    // Find weakest categories for dynamic summary
    const weakestCategories = sections.map((sec, idx) => ({
      name: sec.title,
      id: sec.id,
      score: sectionScores[idx] || 0
    })).sort((a, b) => a.score - b.score).slice(0, 2);
    const summaryText = weakestCategories.length > 0 && weakestCategories[0].score < 70 ? `Your weakest areas are ${weakestCategories[0].name}${weakestCategories.length > 1 ? ` and ${weakestCategories[1].name}` : ''} - addressing these gaps should be your top priority.` : 'You have a solid foundation across all categories.';
    return <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-2578">
        <div className="flex items-center justify-between mb-8" data-id="element-2579">
          <button onClick={() => setView('landing')} className="flex items-center gap-2 text-secondary hover:text-primary transition-colors text-sm font-medium" data-id="element-2580">
            <ArrowLeft className="w-4 h-4" data-id="element-2581" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3" data-id="element-2582">
            <button onClick={() => setShowDownloadModal(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-borderColor rounded-lg text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue transition-colors shadow-sm" data-id="element-2583">
              <Download className="w-4 h-4" data-id="element-2584" />
              Download PDF
            </button>
            <button onClick={() => setShowShareModal(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-borderColor rounded-lg text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue transition-colors shadow-sm" data-id="element-2585">
              <Share2 className="w-4 h-4" data-id="element-2586" />
              Share
            </button>
          </div>
        </div>

        {/* Summary Header Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-2587">
          <div className="bg-white border border-borderColor rounded-xl p-4 shadow-sm" data-id="element-2588">
            <p className="text-xs text-secondary font-medium uppercase tracking-wider mb-1" data-id="element-2589">
              Assessment Type
            </p>
            <p className="text-sm font-bold text-primary capitalize" data-id="element-2590">
              {type} Legal Health
            </p>
          </div>
          <div className="bg-white border border-borderColor rounded-xl p-4 shadow-sm" data-id="element-2591">
            <p className="text-xs text-secondary font-medium uppercase tracking-wider mb-1" data-id="element-2592">
              Date Completed
            </p>
            <p className="text-sm font-bold text-primary" data-id="element-2593">Today</p>
          </div>
          <div className="bg-white border border-borderColor rounded-xl p-4 shadow-sm" data-id="element-2594">
            <p className="text-xs text-secondary font-medium uppercase tracking-wider mb-1" data-id="element-2595">
              Issues Found
            </p>
            <p className="text-sm font-bold text-primary" data-id="element-2596">
              {actionPlanItems.length} Action Items
            </p>
          </div>
          <div className="bg-white border border-borderColor rounded-xl p-4 shadow-sm" data-id="element-2597">
            <p className="text-xs text-secondary font-medium uppercase tracking-wider mb-1" data-id="element-2598">
              Risk Level
            </p>
            <p className={`text-sm font-bold ${score >= 70 ? 'text-green-600' : score >= 40 ? 'text-amber-600' : 'text-destructive'}`} data-id="element-2599">
              {score >= 70 ? 'Low Risk' : score >= 40 ? 'Medium Risk' : 'High Risk'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-2600">
          {/* Main Score Card */}
          <div className="lg:col-span-2 bg-white border border-borderColor rounded-2xl p-8 shadow-sm relative overflow-hidden" data-id="element-2601">
            <div className={`absolute top-0 right-0 w-64 h-64 ${score >= 70 ? 'bg-green-50' : score >= 40 ? 'bg-amber-50' : 'bg-red-50'} rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none`} data-id="element-2602"></div>

            <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-start gap-8" data-id="element-2603">
              <div className="relative flex-shrink-0" data-id="element-2604">
                <svg className="w-40 h-40 transform -rotate-90" data-id="element-2605">
                  <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-borderColor" data-id="element-2606" />
                  <motion.circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="12" fill="transparent" strokeDasharray={440} initial={{
                  strokeDashoffset: 440
                }} animate={{
                  strokeDashoffset: 440 - 440 * score / 100
                }} transition={{
                  duration: 1.5,
                  ease: 'easeOut'
                }} className={score >= 70 ? 'text-green-500' : score >= 40 ? 'text-amber-500' : 'text-destructive'} data-id="element-2607" />
                </svg>
                <AnimatedScore score={score} data-id="element-2608" />
              </div>

              <div className="text-center sm:text-left flex-1" data-id="element-2609">
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full mb-3 uppercase tracking-wide ${score >= 70 ? 'bg-green-100 text-green-800' : score >= 40 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-id="element-2610">
                  <AlertCircle className="w-3.5 h-3.5" data-id="element-2611" />
                  {score >= 70 ? 'Strong Foundation' : score >= 40 ? 'Needs Review' : 'Action Recommended'}
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-3" data-id="element-2612">
                  Your Legal Health Score
                </h1>
                <p className="text-secondary leading-relaxed mb-6" data-id="element-2613">
                  {summaryText}
                </p>
                <button onClick={() => setView('action-plan')} className={`${themeBg} ${themeHoverBg} text-white font-medium py-3 px-6 rounded-xl transition-colors shadow-sm w-full sm:w-auto`} data-id="element-2614">
                  View Personalized Action Plan
                </button>
              </div>
            </div>
          </div>

          {/* Priority Issues */}
          <div className="bg-white border border-borderColor rounded-2xl p-6 shadow-sm flex flex-col" data-id="element-2615">
            <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2" data-id="element-2616">
              <AlertTriangle className={`w-5 h-5 ${score >= 70 ? 'text-green-500' : score >= 40 ? 'text-amber-500' : 'text-destructive'}`} data-id="element-2617" />
              Top Priority Issues
            </h3>
            <div className="space-y-4 flex-grow" data-id="element-2618">
              {actionPlanItems.filter(item => item.priority === 'high').length > 0 ? actionPlanItems.filter(item => item.priority === 'high').slice(0, 3).map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-2619">
                      <div className="w-2 h-2 rounded-full bg-destructive mt-2 flex-shrink-0" data-id="element-2620"></div>
                      <p className="text-sm text-primary font-medium" data-id="element-2621">
                        {item.text}
                      </p>
                    </div>) : actionPlanItems.filter(item => item.priority === 'medium').length > 0 ? actionPlanItems.filter(item => item.priority === 'medium').slice(0, 3).map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-2622">
                      <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 flex-shrink-0" data-id="element-2623"></div>
                      <p className="text-sm text-primary font-medium" data-id="element-2624">
                        {item.text}
                      </p>
                    </div>) : <div className="flex items-center gap-3 text-green-600" data-id="element-2625">
                  <CheckCircle className="w-5 h-5" data-id="element-2626" />
                  <p className="text-sm font-medium" data-id="element-2627">
                    No critical issues found.
                  </p>
                </div>}
            </div>
            <button onClick={() => navigate('/legal-help')} className={`w-full mt-6 bg-white border-2 ${themeBorder} ${themeText} ${themeLightBg} font-medium py-2.5 px-4 rounded-lg transition-colors text-sm`} data-id="element-2628">
              Book Legal Advice Call
            </button>
          </div>
        </div>

        {/* Category Breakdown */}
        <h3 className="text-xl font-bold text-primary mb-6" data-id="element-2629">
          Category Breakdown
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-2630">
          {sections.map((sec, idx) => {
          const Icon = sec.icon;
          const catScore = sectionScores[idx] || 0;
          const statusColor = catScore >= 70 ? 'text-green-500 bg-green-100' : catScore >= 40 ? 'text-amber-500 bg-amber-100' : 'text-destructive bg-red-100';
          const summary = getSectionSummary(sec.id, answers, type, catScore);
          return <div key={sec.id} className="bg-white border border-borderColor rounded-xl p-5 shadow-sm" data-id="element-2631">
                <div className="flex items-center justify-between mb-4" data-id="element-2632">
                  <div className="flex items-center gap-3" data-id="element-2633">
                    <div className={`p-2 rounded-lg ${statusColor}`} data-id="element-2634">
                      <Icon className="w-5 h-5" data-id="element-2635" />
                    </div>
                    <h4 className="font-semibold text-primary" data-id="element-2636">{sec.title}</h4>
                  </div>
                  <span className="text-lg font-bold text-primary" data-id="element-2637">
                    {catScore}%
                  </span>
                </div>
                <div className="w-full bg-borderColor rounded-full h-2 mb-3" data-id="element-2638">
                  <div className={`h-2 rounded-full ${catScore >= 70 ? 'bg-green-500' : catScore >= 40 ? 'bg-amber-500' : 'bg-destructive'}`} style={{
                width: `${catScore}%`
              }} data-id="element-2639"></div>
                </div>
                <p className="text-xs text-secondary leading-relaxed" data-id="element-2640">
                  {summary}
                </p>
              </div>;
        })}
        </div>

        {/* Contextual Recommendations instead of Upsell Banner */}
        <h3 className="text-xl font-bold text-primary mb-6" data-id="element-2641">
          Recommended Next Steps
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2642">
          <div className="bg-white border border-borderColor rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-2643">
            <div className="p-3 bg-aliceBlue rounded-lg w-fit mb-4" data-id="element-2644">
              <FileText className="w-6 h-6 text-violetBlue" data-id="element-2645" />
            </div>
            <h4 className="font-bold text-primary mb-2" data-id="element-2646">
              Create Missing Documents
            </h4>
            <p className="text-sm text-secondary mb-4" data-id="element-2647">
              You have missing legal protections. Use our template library to
              draft what you need.
            </p>
            <button onClick={() => navigate('/all-templates')} className="text-sm font-medium text-violetBlue hover:underline" data-id="element-2648">
              Browse Templates &rarr;
            </button>
          </div>
          <div className="bg-white border border-borderColor rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-2649">
            <div className="p-3 bg-turquoise/10 rounded-lg w-fit mb-4" data-id="element-2650">
              <HelpCircle className="w-6 h-6 text-turquoise" data-id="element-2651" />
            </div>
            <h4 className="font-bold text-primary mb-2" data-id="element-2652">Ask LawVo AI</h4>
            <p className="text-sm text-secondary mb-4" data-id="element-2653">
              Have specific questions about your results? Our AI Copilot can
              provide immediate guidance.
            </p>
            <button onClick={() => navigate('/ai-copilot')} className="text-sm font-medium text-turquoise hover:underline" data-id="element-2654">
              Open AI Copilot &rarr;
            </button>
          </div>
          <div className="bg-white border border-borderColor rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-2655">
            <div className="p-3 bg-red-50 rounded-lg w-fit mb-4" data-id="element-2656">
              <Shield className="w-6 h-6 text-destructive" data-id="element-2657" />
            </div>
            <h4 className="font-bold text-primary mb-2" data-id="element-2658">Talk to a Lawyer</h4>
            <p className="text-sm text-secondary mb-4" data-id="element-2659">
              Your high-priority issues require professional review. Book a
              consultation today.
            </p>
            <button onClick={() => navigate('/legal-help')} className="text-sm font-medium text-destructive hover:underline" data-id="element-2660">
              Book Consultation &rarr;
            </button>
          </div>
        </div>
      </div>;
  };
  const renderActionPlan = () => <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-2661">
      <div className="flex items-center justify-between mb-8" data-id="element-2662">
        <button onClick={() => setView('results')} className="flex items-center gap-2 text-secondary hover:text-primary transition-colors text-sm font-medium" data-id="element-2663">
          <ArrowLeft className="w-4 h-4" data-id="element-2664" />
          Back to Results
        </button>
        <button onClick={() => setShowDownloadModal(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-borderColor rounded-lg text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue transition-colors shadow-sm" data-id="element-2665">
          <Download className="w-4 h-4" data-id="element-2666" />
          Export Plan
        </button>
      </div>

      <div className="mb-8" data-id="element-2667">
        <h1 className="text-3xl font-bold text-primary mb-3" data-id="element-2668">
          Your Action Plan
        </h1>
        <p className="text-secondary text-lg" data-id="element-2669">
          We've prioritized your legal tasks based on your answers. Check them
          off as you complete them to improve your Legal Health Score.
        </p>
      </div>

      <div className="space-y-6" data-id="element-2670">
        {/* High Priority */}
        {actionPlanItems.filter(item => item.priority === 'high').length > 0 && <div data-id="element-2671">
            <div className="flex items-center gap-2 mb-4" data-id="element-2672">
              <AlertTriangle className="w-5 h-5 text-destructive" data-id="element-2673" />
              <h2 className="text-xl font-bold text-primary" data-id="element-2674">
                Immediate Priorities
              </h2>
              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full ml-2" data-id="element-2675">
                Do this week
              </span>
            </div>

            <div className="bg-white border border-borderColor rounded-xl shadow-sm overflow-hidden" data-id="element-2676">
              {actionPlanItems.filter(item => item.priority === 'high').map(item => <div key={item.id} className={`p-5 border-b border-borderColor last:border-b-0 transition-colors ${item.completed ? 'bg-gray-50' : 'hover:bg-aliceBlue'}`} data-id="element-2677">
                    <div className="flex items-start gap-4" data-id="element-2678">
                      <button onClick={() => toggleActionItem(item.id)} className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${item.completed ? 'bg-green-500 border-green-500' : `border-borderColor ${themeBorder}`}`} data-id="element-2679">
                        {item.completed && <Check className="w-4 h-4 text-white" data-id="element-2680" />}
                      </button>
                      <div className="flex-1" data-id="element-2681">
                        <div className="flex items-center gap-2 mb-1" data-id="element-2682">
                          <span className={`text-xs font-semibold ${themeText} uppercase tracking-wider`} data-id="element-2683">
                            {item.category}
                          </span>
                        </div>
                        <h3 className={`text-base font-semibold mb-2 ${item.completed ? 'text-secondary line-through' : 'text-primary'}`} data-id="element-2684">
                          {item.text}
                        </h3>

                        <div className="flex items-center gap-3 mt-2 mb-3" data-id="element-2685">
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-2686">
                            <Zap className="w-3 h-3" data-id="element-2687" /> Effort: {item.effort}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-2688">
                            <Timer className="w-3 h-3" data-id="element-2689" /> Time: {item.time}
                          </span>
                        </div>

                        {!item.completed && <div className="flex items-center gap-3 mt-3" data-id="element-2690">
                            <button onClick={() => navigate(item.actionUrl)} className={`text-sm font-medium text-white ${themeBg} ${themeHoverBg} px-4 py-2 rounded-lg transition-colors`} data-id="element-2691">
                              {item.actionText}
                            </button>
                            <button onClick={() => navigate('/legal-help')} className="text-sm font-medium text-secondary hover:text-primary transition-colors" data-id="element-2692">
                              Need help?
                            </button>
                          </div>}
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>}

        {/* Medium Priority */}
        {actionPlanItems.filter(item => item.priority === 'medium').length > 0 && <div data-id="element-2693">
            <div className="flex items-center gap-2 mb-4 mt-8" data-id="element-2694">
              <AlertCircle className="w-5 h-5 text-amber-500" data-id="element-2695" />
              <h2 className="text-xl font-bold text-primary" data-id="element-2696">
                Recommended Reviews
              </h2>
              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold rounded-full ml-2" data-id="element-2697">
                Do this month
              </span>
            </div>

            <div className="bg-white border border-borderColor rounded-xl shadow-sm overflow-hidden" data-id="element-2698">
              {actionPlanItems.filter(item => item.priority === 'medium').map(item => <div key={item.id} className={`p-5 border-b border-borderColor last:border-b-0 transition-colors ${item.completed ? 'bg-gray-50' : 'hover:bg-aliceBlue'}`} data-id="element-2699">
                    <div className="flex items-start gap-4" data-id="element-2700">
                      <button onClick={() => toggleActionItem(item.id)} className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${item.completed ? 'bg-green-500 border-green-500' : `border-borderColor ${themeBorder}`}`} data-id="element-2701">
                        {item.completed && <Check className="w-4 h-4 text-white" data-id="element-2702" />}
                      </button>
                      <div className="flex-1" data-id="element-2703">
                        <div className="flex items-center gap-2 mb-1" data-id="element-2704">
                          <span className={`text-xs font-semibold ${themeText} uppercase tracking-wider`} data-id="element-2705">
                            {item.category}
                          </span>
                        </div>
                        <h3 className={`text-base font-semibold mb-2 ${item.completed ? 'text-secondary line-through' : 'text-primary'}`} data-id="element-2706">
                          {item.text}
                        </h3>

                        <div className="flex items-center gap-3 mt-2 mb-3" data-id="element-2707">
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-2708">
                            <Zap className="w-3 h-3" data-id="element-2709" /> Effort: {item.effort}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-2710">
                            <Timer className="w-3 h-3" data-id="element-2711" /> Time: {item.time}
                          </span>
                        </div>

                        {!item.completed && <div className="flex items-center gap-3 mt-3" data-id="element-2712">
                            <button onClick={() => navigate(item.actionUrl)} className={`text-sm font-medium ${themeText} ${themeLightBg} hover:bg-opacity-80 px-4 py-2 rounded-lg transition-colors`} data-id="element-2713">
                              {item.actionText}
                            </button>
                          </div>}
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>}

        {/* Low Priority */}
        {actionPlanItems.filter(item => item.priority === 'low').length > 0 && <div data-id="element-2714">
            <div className="flex items-center gap-2 mb-4 mt-8" data-id="element-2715">
              <Info className="w-5 h-5 text-blue-500" data-id="element-2716" />
              <h2 className="text-xl font-bold text-primary" data-id="element-2717">
                Optional Improvements
              </h2>
              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-bold rounded-full ml-2" data-id="element-2718">
                Good to have
              </span>
            </div>

            <div className="bg-white border border-borderColor rounded-xl shadow-sm overflow-hidden" data-id="element-2719">
              {actionPlanItems.filter(item => item.priority === 'low').map(item => <div key={item.id} className={`p-5 border-b border-borderColor last:border-b-0 transition-colors ${item.completed ? 'bg-gray-50' : 'hover:bg-aliceBlue'}`} data-id="element-2720">
                    <div className="flex items-start gap-4" data-id="element-2721">
                      <button onClick={() => toggleActionItem(item.id)} className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${item.completed ? 'bg-green-500 border-green-500' : `border-borderColor ${themeBorder}`}`} data-id="element-2722">
                        {item.completed && <Check className="w-4 h-4 text-white" data-id="element-2723" />}
                      </button>
                      <div className="flex-1" data-id="element-2724">
                        <div className="flex items-center gap-2 mb-1" data-id="element-2725">
                          <span className={`text-xs font-semibold ${themeText} uppercase tracking-wider`} data-id="element-2726">
                            {item.category}
                          </span>
                        </div>
                        <h3 className={`text-base font-semibold mb-2 ${item.completed ? 'text-secondary line-through' : 'text-primary'}`} data-id="element-2727">
                          {item.text}
                        </h3>

                        <div className="flex items-center gap-3 mt-2 mb-3" data-id="element-2728">
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-2729">
                            <Zap className="w-3 h-3" data-id="element-2730" /> Effort: {item.effort}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-2731">
                            <Timer className="w-3 h-3" data-id="element-2732" /> Time: {item.time}
                          </span>
                        </div>

                        {!item.completed && <div className="flex items-center gap-3 mt-3" data-id="element-2733">
                            <button onClick={() => navigate(item.actionUrl)} className={`text-sm font-medium ${themeText} ${themeLightBg} hover:bg-opacity-80 px-4 py-2 rounded-lg transition-colors`} data-id="element-2734">
                              {item.actionText}
                            </button>
                          </div>}
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>}
      </div>
    </div>;
  const renderHistory = () => <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-2735">
      <div className="flex items-center justify-between mb-8" data-id="element-2736">
        <button onClick={() => setView('landing')} className="flex items-center gap-2 text-secondary hover:text-primary transition-colors text-sm font-medium" data-id="element-2737">
          <ArrowLeft className="w-4 h-4" data-id="element-2738" />
          Back to Overview
        </button>
        <button onClick={() => setView('landing')} className="flex items-center gap-2 px-4 py-2 bg-violetBlue text-white rounded-lg text-sm font-medium hover:bg-[#6844E8] transition-colors shadow-sm" data-id="element-2739">
          <Plus className="w-4 h-4" data-id="element-2740" />
          New Assessment
        </button>
      </div>

      <h1 className="text-3xl font-bold text-primary mb-6" data-id="element-2741">
        Assessment History
      </h1>

      <div className="bg-white border border-borderColor rounded-xl shadow-sm overflow-hidden" data-id="element-2742">
        <div className="overflow-x-auto" data-id="element-2743">
          <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-2744">
            <thead data-id="element-2745">
              <tr className="bg-gray-50 border-b border-borderColor" data-id="element-2746">
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider" data-id="element-2747">
                  Type
                </th>
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider" data-id="element-2748">
                  Date
                </th>
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider" data-id="element-2749">
                  Status
                </th>
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider" data-id="element-2750">
                  Score / Progress
                </th>
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider" data-id="element-2751">
                  Top Flagged Area
                </th>
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider text-right" data-id="element-2752">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borderColor" data-id="element-2753">
              {historyItems.map(item => <tr key={item.id} className="hover:bg-aliceBlue transition-colors" data-id="element-2754">
                  <td className="p-4" data-id="element-2755">
                    <div className="flex items-center gap-3" data-id="element-2756">
                      <div className={`p-2 rounded-lg ${item.type === 'personal' ? 'bg-aliceBlue' : 'bg-turquoise/10'}`} data-id="element-2757">
                        {item.type === 'personal' ? <Activity className="w-4 h-4 text-violetBlue" data-id="element-2758" /> : <Building2 className="w-4 h-4 text-turquoise" data-id="element-2759" />}
                      </div>
                      <span className="font-medium text-primary capitalize" data-id="element-2760">
                        {item.type} Check
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-secondary" data-id="element-2761">{item.date}</td>
                  <td className="p-4" data-id="element-2762">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-2763">
                      {item.status === 'completed' ? 'Completed' : 'Draft'}
                    </span>
                  </td>
                  <td className="p-4" data-id="element-2764">
                    <div className="flex items-center gap-2 max-w-[150px]" data-id="element-2765">
                      <div className="flex-1 bg-borderColor rounded-full h-1.5" data-id="element-2766">
                        <div className={`${item.status === 'completed' ? item.score >= 70 ? 'bg-green-500' : item.score >= 40 ? 'bg-amber-500' : 'bg-destructive' : 'bg-violetBlue'} h-1.5 rounded-full`} style={{
                      width: `${item.score}%`
                    }} data-id="element-2767"></div>
                      </div>
                      <span className="text-xs font-medium text-primary w-10" data-id="element-2768">
                        {item.status === 'completed' ? `${item.score}/100` : `${item.score}%`}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-secondary" data-id="element-2769">{item.topFlag}</td>
                  <td className="p-4 text-right" data-id="element-2770">
                    <div className="flex items-center justify-end gap-3" data-id="element-2771">
                      <button onClick={() => {
                    setType(item.type as AssessmentType);
                    if (item.status === 'completed') {
                      generateActionPlan();
                      setView('results');
                    } else {
                      startSetup(item.type as AssessmentType);
                    }
                  }} className="text-sm font-medium text-violetBlue hover:text-[#6844E8] transition-colors" data-id="element-2772">
                        {item.status === 'completed' ? 'View Report' : 'Resume'}
                      </button>
                      <div className="w-px h-4 bg-borderColor" data-id="element-2773"></div>
                      <button className="text-secondary hover:text-primary transition-colors" title="Duplicate" data-id="element-2774">
                        <Copy className="w-4 h-4" data-id="element-2775" />
                      </button>
                      <button className="text-secondary hover:text-primary transition-colors" title="Archive" data-id="element-2776">
                        <Archive className="w-4 h-4" data-id="element-2777" />
                      </button>
                    </div>
                  </td>
                </tr>)}
              {historyItems.length === 0 && <tr data-id="element-2778">
                  <td colSpan={6} className="p-8 text-center text-secondary" data-id="element-2779">
                    No assessment history found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
  return <div className="min-h-screen bg-white" data-id="element-2780">
      <DashboardHeader categories={[]} onMenuClick={toggleMobileSidebar} onNavigateToAICopilot={handleNavigateToAICopilot} onNavigate={handleNavigate} data-id="element-2781" />
      <main data-id="element-2782">
        {view === 'landing' && renderLanding()}
        {view === 'setup' && renderSetup()}
        {view === 'wizard' && renderWizard()}
        {view === 'results' && renderResults()}
        {view === 'action-plan' && renderActionPlan()}
        {view === 'history' && renderHistory()}
      </main>

      {/* Modals */}
      <DownloadReportModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} reportData={{
      type,
      score,
      sectionScores,
      sections: type === 'personal' ? PERSONAL_SECTIONS : BUSINESS_SECTIONS,
      actionPlanItems,
      setupData,
      answers
    }} data-id="element-2783" />

      <ShareReportModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} reportData={{
      type,
      score,
      id: 'req_8f92a1'
    }} onSuccess={showToast} data-id="element-2784" />

      {/* Success Toast */}
      <AnimatePresence data-id="element-2785">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-6 right-6 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-xl flex items-center gap-3 z-50" data-id="element-2786">
            <CheckCircle className="w-5 h-5 text-green-400" data-id="element-2787" />
            <span className="text-sm font-medium" data-id="element-2788">{toastMessage}</span>
          </motion.div>}
      </AnimatePresence>
    </div>;
}